import type { CardDef } from '../../../../core/card';
import { ACID } from '../../../palette';
import { pt } from '../geom';
import { line, solid, type FaceSpec, type Prim } from '../spec';
import { docketFrame } from './frame';

/**
 * **断裂 Break —— `SIGTRAP` 断点。**
 *
 * 操作是**切断 + 错位**：一条粗线被切开，断口两侧上下错开；缺口上套一个
 * 空心方块＝断点标记；虚线画出"本该走的路径"（那条线其实是直的）。
 *
 * ⚠️ 这组几何是**全牌库唯一一组可以真空心理解的**：
 *    一条断成两截、还错开的线，不需要任何文字说明就知道"这里出了问题"。
 *    所以它一句图注都不加 —— 加了反而把"一眼就懂"稀释成"要读才知道"。
 *
 * 背景那条刻度轨像执行轨：断续的短竖线，越往右越稀。
 */
export function breakFace(card: CardDef): FaceSpec {
  /**
   * 断点的墨色。评审定的：**酸玫红**（`ACID.rose`）。
   *
   * ⚠️ 它和 `SIGNAL.danger`（异常牌 / 濒死那一族的红）**同一个色号** —— 这是
   *    有意的：断点就是"把别人的行动掐掉"，在这一版的语言里它属于"危险"那一族。
   *    ⚠️ 而未画图的打断牌（扩展包塞进来的）仍然拿 `placeholder.accentFor()`
   *    的 `info`（酸青）—— 那是"按行为给辨识色"的通用规则，两处不必一致。
   */
  const ink = ACID.rose;
  const art: Prim[] = [];

  // ---- 背景：执行轨 ----
  art.push(line([pt(-24, 86), pt(124, 86)], { width: 0.5, color: ink, alpha: 0.24 }));
  for (let i = 0; i < 20; i++) {
    const x = -20 + i * 7.4;
    art.push(
      line([pt(x, 86), pt(x, i % 3 === 0 ? 79 : 82.5)], { width: 0.5, color: ink, alpha: 0.24 }),
    );
  }

  // ---- 本该走的路径：一条直的虚线，穿过整个缺口 ----
  art.push(
    line([pt(52, 51), pt(126, 51)], { width: 1, color: ink, alpha: 0.33, dash: [3.2, 3.4] }),
  );
  // ---- 断成两截的粗线，右半截往下错开 12 ----
  art.push(line([pt(-26, 51), pt(49, 51)], { width: 4.2, color: ink }));
  art.push(line([pt(63, 63), pt(126, 63)], { width: 4.2, color: ink }));
  // ---- 断口上的空心方块 ----
  art.push({
    k: 'rect',
    x: 44.5,
    y: 46,
    w: 23,
    h: 23,
    fill: solid(0x000000),
    stroke: { width: 2.4, color: ink },
  });

  return {
    ground: solid(0x000000),
    accent: ink,
    prims: docketFrame(card, ink, []),
    press: true,
    // 刻度轨和粗线都伸到牌外 —— 线是"从这里穿过去"的，不该在牌边停住
    spill: art,
  };
}
